// MenuPrincipal.tsx - IBIRACO Dashboard Hub (MULTI-CIDADES)
import React, { useEffect, useState } from 'react';
import { db, auth } from './firebaseConfig';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { 
  LogOut, 
  TrendingUp, 
  Award, 
  Activity, 
  MapIcon,
  Megaphone,
  Users,
  Star,
  AlertTriangle,
  CheckCircle,
  Clock
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function MenuPrincipal() {
  const navigate = useNavigate();
  
  // Estados da cidade do admin
  const [cidadeDisplay, setCidadeDisplay] = useState<string>('');
  const [cidadeNormalizada, setCidadeNormalizada] = useState<string>('');
  
  // Estados de métricas
  const [stats, setStats] = useState({
    pendentes: 0,
    emObra: 0,
    resolvidos: 0,
    totalReportes: 0,
    enquetesAtivas: 0,
    totalCondecoracoes: 0
  });
  
  const [loading, setLoading] = useState(true);

  // ============================================================================
  // 🔄 INICIALIZAÇÃO
  // ============================================================================
  
  useEffect(() => {
    const cidadeDisplayLS = localStorage.getItem('cidadeDisplay');
    const cidadeNormalizadaLS = localStorage.getItem('cidadeNormalizada');
    
    if (cidadeDisplayLS && cidadeNormalizadaLS) {
      setCidadeDisplay(cidadeDisplayLS);
      setCidadeNormalizada(cidadeNormalizadaLS);
      setLoading(false);
    } else {
      const unsubscribe = auth.onAuthStateChanged((user) => {
        if (user) {
          const email = user.email || '';
          if (email.includes('mariocampos')) {
            setCidadeDisplay('Mário Campos');
            setCidadeNormalizada('mario_campos');
          } else if (email.includes('sarzedo')) {
            setCidadeDisplay('Sarzedo');
            setCidadeNormalizada('sarzedo');
          } else {
            setCidadeDisplay('Ibirité');
            setCidadeNormalizada('ibirite');
          }
          setLoading(false);
        }
      });
      return () => unsubscribe();
    }
  }, []);

  // ============================================================================
  // 🔄 MONITORAR MÉTRICAS
  // ============================================================================
  
  useEffect(() => {
    if (!cidadeNormalizada) return;

    // Monitorar Reportes
    const cidadesBusca = cidadeNormalizada === "ibirite"
      ? ["ibirite", "Ibirité"]
      : [cidadeNormalizada];

    const qReportes = query(
      collection(db, "reportes"),
      where("cidadeReporte", "in", cidadesBusca)
    );
    
    const unsubReportes = onSnapshot(qReportes, (snapshot) => {
      const docs = snapshot.docs.map(d => d.data());
      setStats(prev => ({
        ...prev,
        pendentes: docs.filter((d: any) => d.status === 'Pendente').length,
        emObra: docs.filter((d: any) => d.status === 'Em obra').length,
        resolvidos: docs.filter((d: any) => d.status === 'Resolvido').length,
        totalReportes: snapshot.size
      }));
    });

    // Monitorar Enquetes Ativas
    const qEnquetes = query(
      collection(db, "enquetes"),
      where("cidade", "==", cidadeNormalizada),
      where("ativa", "==", true)
    );
    
    const unsubEnquetes = onSnapshot(qEnquetes, (snapshot) => {
      setStats(prev => ({ ...prev, enquetesAtivas: snapshot.size }));
    });

    // Monitorar Condecorações (total concedidas)
    const qCondecoracoes = query(
      collection(db, "condecoracoes"),
      where("cidade", "==", cidadeNormalizada),
      where("userId", "!=", null)
    );
    
    const unsubCondecoracoes = onSnapshot(qCondecoracoes, (snapshot) => {
      setStats(prev => ({ ...prev, totalCondecoracoes: snapshot.size }));
    });

    return () => {
      unsubReportes();
      unsubEnquetes();
      unsubCondecoracoes();
    };
  }, [cidadeNormalizada]);

  // ============================================================================
  //  RENDERIZAÇÃO
  // ============================================================================

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F4F7FE] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#6200EE] mx-auto mb-4"></div>
          <p className="text-gray-600 font-bold">Carregando dashboard...</p>
        </div>
      </div>
    );
  }

  const taxaResolucao = stats.totalReportes > 0 
    ? Math.round((stats.resolvidos / stats.totalReportes) * 100) 
    : 0;

  return (
    <div className="min-h-screen bg-[#F4F7FE]">
      
      {/* ============================================================================
          BARRA DE NAVEGAÇÃO SUPERIOR
      ============================================================================ */}
      <header className="bg-white shadow-sm border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex justify-between items-center h-20">
            
            {/* Logo e Cidade */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3">
                <div className="bg-[#6200EE] p-2 rounded-xl">
                  <span className="text-2xl font-black text-white italic">B</span>
                </div>
                <div>
                  <h1 className="text-xl font-black text-[#6200EE] italic tracking-tighter uppercase">
                    IBIRACO<span className="text-xs align-top text-[#e67e22]">®</span>
                  </h1>
                  <p className="text-[10px] font-bold text-gray-500 uppercase">
                    Prefeitura de {cidadeDisplay}
                  </p>
                </div>
              </div>
            </div>

            {/* Links de Navegação */}
            <nav className="hidden md:flex items-center gap-2">
              <button 
                onClick={() => navigate('/dashboard')}
                className="flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-sm text-gray-600 hover:bg-[#6200EE] hover:text-white transition-all"
              >
                <Activity size={18} />
                Zeladoria
              </button>
              
              <button 
                onClick={() => navigate('/mapa-atividade')}
                className="flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-sm text-gray-600 hover:bg-[#e67e22] hover:text-white transition-all"
              >
                <MapIcon size={18} />
                Mapa
              </button>
              
              <button 
                onClick={() => navigate('/enquetes')}
                className="flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-sm text-gray-600 hover:bg-[#e67e22] hover:text-white transition-all"
              >
                <Megaphone size={18} />
                Enquetes
              </button>
              
              <button 
                onClick={() => navigate('/condecoracoes')}
                className="flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-sm text-gray-600 hover:bg-[#FFD700] hover:text-[#1A1C1D] transition-all"
              >
                <Award size={18} />
                Condecorações
              </button>
            </nav>

            {/* Logout */}
            <button 
              onClick={() => auth.signOut()}
              className="flex items-center gap-2 bg-red-50 text-red-600 px-4 py-2 rounded-xl font-bold text-sm hover:bg-red-100 transition-all"
            >
              <LogOut size={18} />
              <span className="hidden md:inline">Sair</span>
            </button>
          </div>
        </div>

        {/* Menu Mobile */}
        <div className="md:hidden border-t border-gray-100 bg-gray-50">
          <div className="flex flex-wrap justify-center gap-2 p-3">
            <button 
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-1 px-3 py-2 rounded-lg font-bold text-xs bg-white shadow-sm"
            >
              <Activity size={14} /> Zeladoria
            </button>
            <button 
              onClick={() => navigate('/mapa-atividade')}
              className="flex items-center gap-1 px-3 py-2 rounded-lg font-bold text-xs bg-white shadow-sm"
            >
              <MapIcon size={14} /> Mapa
            </button>
            <button 
              onClick={() => navigate('/enquetes')}
              className="flex items-center gap-1 px-3 py-2 rounded-lg font-bold text-xs bg-white shadow-sm"
            >
              <Megaphone size={14} /> Enquetes
            </button>
            <button 
              onClick={() => navigate('/condecoracoes')}
              className="flex items-center gap-1 px-3 py-2 rounded-lg font-bold text-xs bg-white shadow-sm"
            >
              <Award size={14} /> Condecorações
            </button>
          </div>
        </div>
      </header>

      {/* ============================================================================
          CONTEÚDO PRINCIPAL
      ============================================================================ */}
      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        
        {/* Boas-vindas */}
        <div className="mb-8">
          <h2 className="text-3xl font-black text-[#1A1C1D] mb-2">
            Bem-vindo, {auth.currentUser?.displayName || 'Administrador'}! 👋
          </h2>
          <p className="text-gray-500 font-medium">
            Painel de controle da Prefeitura de {cidadeDisplay}
          </p>
        </div>

        {/* Cards de Métricas */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-[2rem] p-6 shadow-sm border-l-4 border-red-500">
            <div className="flex items-center justify-between mb-2">
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
            <p className="text-gray-500 text-xs font-bold uppercase mb-1">Pendentes</p>
            <p className="text-3xl font-black text-red-600">{stats.pendentes}</p>
          </div>

          <div className="bg-white rounded-[2rem] p-6 shadow-sm border-l-4 border-yellow-500">
            <div className="flex items-center justify-between mb-2">
              <Clock className="w-8 h-8 text-yellow-500" />
            </div>
            <p className="text-gray-500 text-xs font-bold uppercase mb-1">Em Obra</p>
            <p className="text-3xl font-black text-yellow-600">{stats.emObra}</p>
          </div>

          <div className="bg-white rounded-[2rem] p-6 shadow-sm border-l-4 border-green-500">
            <div className="flex items-center justify-between mb-2">
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
            <p className="text-gray-500 text-xs font-bold uppercase mb-1">Resolvidos</p>
            <p className="text-3xl font-black text-green-600">{stats.resolvidos}</p>
          </div>

          <div className="bg-white rounded-[2rem] p-6 shadow-sm border-l-4 border-[#6200EE]">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="w-8 h-8 text-[#6200EE]" />
            </div>
            <p className="text-gray-500 text-xs font-bold uppercase mb-1">Taxa Resolução</p>
            <p className="text-3xl font-black text-[#6200EE]">{taxaResolucao}%</p>
          </div>
        </div>

        {/* Cards de Acesso Rápido */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          
          {/* Zeladoria */}
          <button 
            onClick={() => navigate('/dashboard')}
            className="bg-white p-8 rounded-[2.5rem] shadow-sm border-2 border-transparent hover:border-[#6200EE] transition-all text-left group"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="bg-[#6200EE] p-4 rounded-2xl group-hover:scale-110 transition-transform">
                <Activity size={32} className="text-white" />
              </div>
              <div className="bg-[#6200EE] text-white text-xs font-black px-3 py-1 rounded-full">
                {stats.totalReportes} reportes
              </div>
            </div>
            <h3 className="text-xl font-black text-[#1A1C1D] mb-2">Zeladoria Urbana</h3>
            <p className="text-gray-500 text-sm mb-4">
              Gerencie reportes de buracos, iluminação, lixo e entulho
            </p>
            <div className="flex items-center text-[#6200EE] font-bold text-sm">
              Acessar <span className="ml-2">→</span>
            </div>
          </button>

          {/* Mapa de Atividades */}
          <button 
            onClick={() => navigate('/mapa-atividade')}
            className="bg-white p-8 rounded-[2.5rem] shadow-sm border-2 border-transparent hover:border-[#e67e22] transition-all text-left group"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="bg-[#e67e22] p-4 rounded-2xl group-hover:scale-110 transition-transform">
                <MapIcon size={32} className="text-white" />
              </div>
            </div>
            <h3 className="text-xl font-black text-[#1A1C1D] mb-2">Mapa de Atividades</h3>
            <p className="text-gray-500 text-sm mb-4">
              Visualize no mapa todos os reportes e eventos da cidade
            </p>
            <div className="flex items-center text-[#e67e22] font-bold text-sm">
              Acessar <span className="ml-2">→</span>
            </div>
          </button>

          {/* Enquetes */}
          <button 
            onClick={() => navigate('/enquetes')}
            className="bg-white p-8 rounded-[2.5rem] shadow-sm border-2 border-transparent hover:border-[#e67e22] transition-all text-left group"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="bg-[#e67e22] p-4 rounded-2xl group-hover:scale-110 transition-transform">
                <Megaphone size={32} className="text-white" />
              </div>
              <div className="bg-[#e67e22] text-white text-xs font-black px-3 py-1 rounded-full">
                {stats.enquetesAtivas} ativas
              </div>
            </div>
            <h3 className="text-xl font-black text-[#1A1C1D] mb-2">Enquetes</h3>
            <p className="text-gray-500 text-sm mb-4">
              Crie pesquisas e consulte a opinião dos cidadãos
            </p>
            <div className="flex items-center text-[#e67e22] font-bold text-sm">
              Acessar <span className="ml-2">→</span>
            </div>
          </button>

          {/* Condecorações */}
          <button 
            onClick={() => navigate('/condecoracoes')}
            className="bg-white p-8 rounded-[2.5rem] shadow-sm border-2 border-transparent hover:border-[#FFD700] transition-all text-left group"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="bg-[#FFD700] p-4 rounded-2xl group-hover:scale-110 transition-transform">
                <Award size={32} className="text-[#1A1C1D]" />
              </div>
              <div className="bg-[#FFD700] text-[#1A1C1D] text-xs font-black px-3 py-1 rounded-full">
                {stats.totalCondecoracoes} concedidas
              </div>
            </div>
            <h3 className="text-xl font-black text-[#1A1C1D] mb-2">Condecorações</h3>
            <p className="text-gray-500 text-sm mb-4">
              Reconheça cidadãos exemplares com medalhas e troféus
            </p>
            <div className="flex items-center text-[#FFD700] font-bold text-sm">
              Acessar <span className="ml-2">→</span>
            </div>
          </button>

          {/* Comunicados */}
          <button 
            onClick={() => navigate('/mapa-atividade')}
            className="bg-white p-8 rounded-[2.5rem] shadow-sm border-2 border-transparent hover:border-[#006133] transition-all text-left group"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="bg-[#006133] p-4 rounded-2xl group-hover:scale-110 transition-transform">
                <Megaphone size={32} className="text-white" />
              </div>
            </div>
            <h3 className="text-xl font-black text-[#1A1C1D] mb-2">Comunicados</h3>
            <p className="text-gray-500 text-sm mb-4">
              Publique eventos e avisos importantes no mapa
            </p>
            <div className="flex items-center text-[#006133] font-bold text-sm">
              Acessar <span className="ml-2">→</span>
            </div>
          </button>

          {/* Em Breve - Caçamba */}
          <div className="bg-gray-50 p-8 rounded-[2.5rem] shadow-sm border-2 border-dashed border-gray-300 opacity-60">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-gray-300 p-4 rounded-2xl">
                <Users size={32} className="text-gray-500" />
              </div>
            </div>
            <h3 className="text-xl font-black text-gray-600 mb-2">Caçamba</h3>
            <p className="text-gray-400 text-sm mb-4">
              Em breve: Gerencie solicitações de caçamba
            </p>
            <div className="flex items-center text-gray-400 font-bold text-sm">
              Indisponível
            </div>
          </div>
        </div>

        {/* Resumo Rápido */}
        <div className="mt-8 bg-gradient-to-r from-[#6200EE] to-[#4800b2] rounded-[2.5rem] p-8 text-white shadow-xl">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-2xl font-black mb-2">Resumo da Cidade</h3>
              <p className="text-white/80 font-medium">
                {cidadeDisplay} • {stats.totalReportes} reportes total
              </p>
            </div>
            <div className="text-right">
              <p className="text-5xl font-black">{taxaResolucao}%</p>
              <p className="text-sm font-bold text-white/60 uppercase">Resolvidos</p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}